/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Action, ActionCreators } from './actions';
import Config from '../../shared/entities/config';
import get from 'lodash/get';
export interface State {
  readonly appConfig: Config;
  readonly isInitialConfigRequestFinished: boolean;
  readonly isRequestingConfig: boolean;
  readonly requestingConfigError?: string;
  readonly isLoginConfigLoaded: boolean;
  readonly compactExportEnabled: boolean | null;
  readonly isRequestingCompactExportEnabled: boolean;
  readonly requestingCompactExportEnabledError?: string;
}

export const initialConfigState: State = {
  appConfig: {
    apiPollingIntervalSeconds: 15,
    whatsNewPanelPollingIntervalSeconds: 300,
    useSse: false,
    googleMapsAPIKey: '',
    googleMapsURL: 'https://maps.googleapis.com/maps/api/js?key=',
    terminalCountKillSwitchEnabled: true,
    mptAuthKey: '',
    isFullTailViewEnabled: false,
    isTrafficEndpointEnabled: true,
    isSatellitesInFOVEnabled: false,
    isNextSatellitePanelEnabled: false,
    isMptEnabled: true,
    isTerminalInBeamEnabled: true,
    isHCXEnabled: true,
    isGxEnabled: true,
    hcxHnaRequestOverrideColumnVisible: false,
    isHeatMapsEnabled: false,
    isTthoEnabled: false,
    weatherOverlayConfig: {
      apiUrl: '',
      apiKey: '',
      tileWidth: 256,
      tileHeight: 256,
      refreshPeriodMinutes: 15,
      providerName: '',
      providerLink: '',
    },
    salesforceBaseURL: '',
    chartsConfig: {
      timelineGapSec: 210,
      speedAndAltitudeSec: 180,
      flRateGapSec: 240,
      rlRateGapSec: 240,
      pingGapSec: 300,
      trafficCompGapSec: 240,
      antennaPointAnglesGapSec: 180,
      cumDataUsageGapSec: 240,
    },
    feedbackEmail: '',
    hnarequestEmail: '',
    customerSupportEmail: '',
    serviceTierChangeRequestEmail: '',
    specialBeamsNetworkMapping: {
      ka: [],
      hcx: [],
      vs3: [],
      gx: [],
    },
    loginConfig: {
      oidcIssuer: '',
      oidcClientId: '',
      helpEmail: '',
    },
    gpsSaPollingIntervalSeconds: 300,
    haloNetPositionFallbackAfterSeconds: 60,
    haloNetPollingIntervalSeconds: 1800,
  },
  isInitialConfigRequestFinished: false,
  isRequestingConfig: false,
  requestingConfigError: undefined,
  isLoginConfigLoaded: false,
  compactExportEnabled: null,
  isRequestingCompactExportEnabled: false,
  requestingCompactExportEnabledError: undefined,
};

// Reducer
export default function reducer(state: State = initialConfigState, action: Action): State {
  let partialState: Partial<State> | undefined;
  switch (action.type) {
    case ActionCreators.RequestConfig.type:
      partialState = {
        isRequestingConfig: true,
        isInitialConfigRequestFinished: false,
        requestingConfigError: undefined,
      };
      break;

    case ActionCreators.ReceivedConfigSuccess.type: {
      partialState = {
        appConfig: action.payload.response,
        requestingConfigError: undefined,
        isInitialConfigRequestFinished: true,
        isRequestingConfig: false,
        isLoginConfigLoaded: true,
      };
      break;
    }
    case ActionCreators.ReceivedConfigError.type:
      partialState = {
        requestingConfigError: get(action, 'payload.error_description', 'Unknown error'),
        isRequestingConfig: false,
        isInitialConfigRequestFinished: true,
      };
      break;
    case ActionCreators.ClearReceivedConfigError.type:
      partialState = {
        requestingConfigError: undefined,
      };
      break;
    case ActionCreators.RequestCompactExportEnabled.type:
      partialState = {
        isRequestingCompactExportEnabled: true,
        requestingCompactExportEnabledError: undefined,
      };
      break;
    case ActionCreators.ReceivedCompactExportEnabledSuccess.type:
      partialState = {
        compactExportEnabled: action.payload.response,
        isRequestingCompactExportEnabled: false,
        requestingCompactExportEnabledError: undefined,
      };
      break;
    case ActionCreators.ReceivedCompactExportEnabledError.type:
      partialState = {
        requestingCompactExportEnabledError: action.payload,
        isRequestingCompactExportEnabled: undefined,
      };
      break;
    case ActionCreators.ClearReceivedCompactExportEnabledError.type:
      partialState = {
        requestingCompactExportEnabledError: undefined,
      };
      break;

    // Login config handlers
    case ActionCreators.RequestLoginConfig.type:
      partialState = {
        requestingConfigError: undefined,
      };
      break;
    case ActionCreators.ReceivedLoginConfigSuccess.type: {
      partialState = {
        appConfig: {
          ...state.appConfig,
          loginConfig: action.payload.response,
        },
        isLoginConfigLoaded: true,
      };
      break;
    }
    case ActionCreators.ReceivedLoginConfigError.type:
      partialState = {
        requestingConfigError: get(action, 'payload.error_description', 'Unknown error'),
        isLoginConfigLoaded: false,
      };
      break;

    default:
      return state;
  }
  return { ...state, ...partialState };
}
